import React, { useState, useRef } from "react";
import { IconButton, Loader, Tooltip } from "@vibe/core";
import { IoSync } from "react-icons/io5";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import { formatDate, formatRelativeTime } from "../../../utils/unixDateFormatter";
import logger from "../../../lib/logger";
import { itemViewResync } from "../../../utils/requests/itemViewResync";
import useTokenHook from "../../../hooks/context/useTokenHook";
import {
  findMostRecentChange,
  formatColumnValueItemResync,
  SLOW_RESYNC_COLUMN_TYPES,
  RESYNC_TOAST_INFO_DURATION_MS,
  RESYNC_TOAST_SLOW_INFO_DURATION_MS,
  RESYNC_TOAST_SUCCESS_DURATION_MS,
  RESYNC_TOAST_ERROR_DURATION_MS,
} from "../../../utils/resync/itemResync/itemResyncUtils";
import { UserAvatar } from "../../ui/UserAvatar";
import { RegisterForcePush } from "./useForcePushTracker";

const ItemViewResyncButton = ({
  values,
  columnType,
  columnTitle,
  handleMutateItemLocations,
  handleMutateItemDiscrepancy,
  handleRevalidateItemView,
  setColumnsResyncing,
  registerForcePush,
}: {
  values: any;
  columnType: string;
  columnTitle: string;
  handleMutateItemLocations: (columnType: string, columnTitle: string, latestValue: any) => void;
  handleMutateItemDiscrepancy: (columnType: string, columnTitle: string, latestValue: any) => void;
  handleRevalidateItemView: () => Promise<void>;
  setColumnsResyncing: (columnKeys: string[], isResyncing: boolean) => void;
  // Hands a resync the backend queued through orchestration (202) to the tracker that waits
  // for its socket completion. Optional so the button still works where no tracker is mounted.
  registerForcePush?: RegisterForcePush;
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [changedAt, setChangedAt] = useState<string | null>(null);
  const [latestValue, setLatestValue] = useState<any | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { token } = useTokenHook();
  const requestIdRef = useRef(0);

  const isSlowColumn = SLOW_RESYNC_COLUMN_TYPES.has(columnType);

  const fetchLatestColumnUpdate = async (linkedValues: any[]) => {
    const requestId = ++requestIdRef.current;
    const { itemIds, boardIds, columnIds } = linkedValues.reduce(
      (acc: any, value: any) => {
        acc.itemIds.push(parseInt(value.itemId));
        acc.boardIds.push(parseInt(value.boardId));
        acc.columnIds.push(value.id);
        return acc;
      },
      { itemIds: [], boardIds: [], columnIds: [] },
    );

    try {
      setLoading(true);
      setLoadError(null);

      const response = await MondayApiQueryInstance.getColumnUpdateLogBatch(
        boardIds,
        itemIds,
        columnIds,
      );

      if (requestId !== requestIdRef.current) return;

      const mostRecentChange = findMostRecentChange(response);

      if (!mostRecentChange) {
        setUser(null);
        setChangedAt(null);
        setLatestValue(null);
        return;
      }

      // Commit the value before the avatar lookup. That lookup is decorative, and awaiting it
      // here meant any failure inside it — such as a field Monday removed from the User type —
      // skipped these setters and rendered "no latest value" over a value we already had.
      setChangedAt(mostRecentChange?.created_at);
      setLatestValue(mostRecentChange?.parsedData);

      try {
        // getUsersByUserId takes number[]. activity_logs.user_id is a String, and passing it
        // raw made JSON.stringify emit `users(ids: "-4")` — a quoted scalar where the API wants
        // a list — which monday rejects with the constant "Graphql validation errors". That
        // threw before the value was committed, which is how every column in this modal came to
        // report "No latest value found". ResynchronizeAllColumnsModal always got this right.
        const userResponse = await MondayApiQueryInstance.getUsersByUserId([
          Number(mostRecentChange.user_id),
        ]);

        if (requestId !== requestIdRef.current) return;

        setUser(userResponse?.[0] ?? null);
      } catch (error: any) {
        if (requestId !== requestIdRef.current) return;

        setUser(null);
        logger.error("Resync avatar lookup failed; showing value without author", {
          message: error?.message,
          columnType,
          columnTitle,
        });
      }
    } catch (error: any) {
      if (requestId !== requestIdRef.current) return;

      setUser(null);
      setChangedAt(null);
      setLatestValue(null);
      setLoadError(error?.message ?? "Unknown error");
      // Logged with the query inputs: the previous logger.log(error.message) shipped a bare
      // "Graphql validation errors" with nothing to trace it back to.
      logger.error("Resync latest-value lookup failed", {
        message: error?.message,
        columnType,
        columnTitle,
        boardIds,
        itemIds,
        columnIds,
      });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    if (values) {
      fetchLatestColumnUpdate(values);
    }
  };

  const handleResync = async () => {
    const valueToApply = latestValue;
    const linkedValues = values;
    const columnKey = `${columnType}_${columnTitle}`;

    // Optimistically apply the resynced state and dismiss the modal so the
    // user can keep working. The API call below can take many seconds for
    // file/doc columns because Monday re-uploads each attachment per linked
    // item — there's no UX reason to block the user behind a spinner for it.
    handleMutateItemLocations(columnType, columnTitle, valueToApply);
    handleMutateItemDiscrepancy(columnType, columnTitle, valueToApply);
    setShowModal(false);

    // For slow columns the file/doc value display can't be optimistically
    // updated (per-item URLs don't exist until Monday finishes re-uploading),
    // so swap the green discrepancy badge for a spinner until reconciliation
    // completes — gives the user something to look at besides a stale value.
    if (isSlowColumn) {
      setColumnsResyncing([columnKey], true);
    }

    MondayApiQueryInstance.mondayToastNotification(
      isSlowColumn
        ? `Resyncing ${columnTitle} in the background. ${columnType} columns can take a moment…`
        : `Resyncing ${columnTitle}…`,
      "info",
      isSlowColumn ? RESYNC_TOAST_SLOW_INFO_DURATION_MS : RESYNC_TOAST_INFO_DURATION_MS,
    );

    // A 202 means the backend queued the resync as orchestration jobs instead of writing
    // synchronously. The write lands later; the tracker owns the spinner and the
    // revalidation from here, driven by the socket completion.
    let queued = false;

    try {
      const response = await itemViewResync(token!, linkedValues!, valueToApply);

      if (response?.status === 202 && registerForcePush) {
        queued = true;
        registerForcePush(response.data?.requestId, [columnKey], isSlowColumn);

        MondayApiQueryInstance.mondayToastNotification(
          `${columnTitle} resync queued`,
          "info",
          RESYNC_TOAST_INFO_DURATION_MS,
        );
        return;
      }

      MondayApiQueryInstance.mondayToastNotification(
        `${columnTitle} resynced`,
        "success",
        RESYNC_TOAST_SUCCESS_DURATION_MS,
      );
    } catch (error: any) {
      logger.error("Error resyncing latest change:", error.message);
      MondayApiQueryInstance.mondayToastNotification(
        `Error resyncing ${columnTitle}: ${error.message}.`,
        "error",
        RESYNC_TOAST_ERROR_DURATION_MS,
      );
    } finally {
      if (!queued) {
        // Reconcile the optimistic UI with server truth. On success this confirms
        // the new state and forces file/doc columns to re-fetch from Monday's
        // SDK; on failure this reverts the optimistic discrepancy clear so the
        // resync badge reappears and the user can retry. Keep the in-flight
        // spinner on through revalidation so the icon transitions straight from
        // spinner → final state (check or warning) with no flicker.
        handleRevalidateItemView().finally(() => {
          if (isSlowColumn) {
            setColumnsResyncing([columnKey], false);
          }
        });
      }
    }
  };

  return (
    <>
      <IconButton
        icon={IoSync}
        size="small"
        tooltipContent="Resync"
        kind="tertiary"
        onClick={handleOpenModal}
      />

      <Modal id="resync-modal" size="medium" show={showModal} onClose={() => setShowModal(false)}>
        <ModalBasicLayout>
          <ModalHeader title="Resync" className="px-8" />
          <ModalContent>
            {loading ? (
              <div className="flex justify-center items-center mx-auto">
                <Loader size="small" color="primary" />
              </div>
            ) : loadError ? (
              <div className="flex flex-col gap-3 items-center mx-auto text-center">
                <p className="text-sm text-balance">{"Couldn't load the latest value."}</p>
                <p className="text-sm text-light-secondary-text-color">{loadError}</p>
                <button
                  type="button"
                  className="text-sm underline"
                  onClick={() => values && fetchLatestColumnUpdate(values)}
                >
                  Retry
                </button>
              </div>
            ) : latestValue ? (
              <div className="flex flex-col gap-4 px-3">
                <div>
                  <p>
                    The latest value for <span className="font-bold">{columnTitle}</span> is:
                  </p>
                </div>
                <div className="flex gap-6">
                  <Tooltip
                    content={formatColumnValueItemResync(
                      columnType,
                      latestValue?.value,
                      latestValue?.textual_value,
                      true,
                    )}
                  >
                    <div className="flex overflow-hidden items-center max-w-[200px] cursor-pointer">
                      <div className="flex overflow-hidden min-w-0 font-medium whitespace-nowrap text-ellipsis">
                        {formatColumnValueItemResync(
                          columnType,
                          latestValue?.value,
                          latestValue?.textual_value,
                        )}
                      </div>
                    </div>
                  </Tooltip>
                  <div className="cursor-pointer">
                    <Tooltip
                      content={
                        user
                          ? `Updated by ${user?.name} on ${changedAt ? formatDate(changedAt) : ""}`
                          : `Updated on ${changedAt ? formatDate(changedAt) : ""}`
                      }
                    >
                      <div className="flex gap-3 items-center">
                        {user && (
                          <UserAvatar
                            name={user?.name || ""}
                            photoUrl={user?.photo_thumb}
                            size="md"
                            className="-mx-1 border-2 border-[#f5f6f8] cursor-pointer"
                          />
                        )}

                        <span>{changedAt ? formatRelativeTime(changedAt) : ""}</span>
                      </div>
                    </Tooltip>
                  </div>
                </div>
                <div>
                  <p className="">
                    Would you like to resynchronize this column to all connected items?
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center mx-auto">
                <p className="text-sm text-balance">
                  {`No recorded changes for ${columnTitle} on any connected item. Values set when the item was created don't appear in Monday's column history.`}
                </p>
              </div>
            )}
          </ModalContent>
        </ModalBasicLayout>

        <ModalFooter
          primaryButton={{
            onClick: handleResync,
            text: "Confirm",
            disabled: loading || !latestValue,
          }}
          secondaryButton={{
            onClick: () => setShowModal(false),
            text: "Cancel",
          }}
        />
      </Modal>
    </>
  );
};

export default ItemViewResyncButton;
